/**
 * @file functionsService.ts
 * @path packages/core/src/firebase/functions.service.ts
 * @type Service
 * @description Business logic and data access for Functions
 */

// The Firebase SDK is loaded when a function is first called, not when this
// module is imported: contact forms and lead magnets sit on pages that should
// paint before any of the SDK's ~130 KB has been downloaded.

export const FunctionsService = {
  /**
   * Call a Firebase Cloud Function by name
   * @param functionName - The name of the function deployed to Firebase
   * @param data - The payload to send to the function
   */
  call: async <TRequest = unknown, TResponse = unknown>(
    functionName: string,
    data?: TRequest,
  ): Promise<TResponse> => {
    try {
      const [{ httpsCallable }, { functions, getFunctionsInstance }] =
        await Promise.all([
          import("firebase/functions"),
          import("./firebaseClient.config"),
        ]);
      // The eager instance is undefined when Firebase config was missing at
      // page load (previews, e2e); create it on first use instead of passing
      // undefined to httpsCallable.
      const functionRef = httpsCallable<TRequest, TResponse>(
        functions ?? getFunctionsInstance(),
        functionName,
      );
      const result = await functionRef(data);
      return result.data;
    } catch (error) {
      console.error(`Error calling function ${functionName}:`, error);
      throw error;
    }
  },
};
